/**
 * Storage Mappers
 *
 * Pure functions to convert database rows to domain types.
 * These are storage-agnostic and can be used by any storage provider
 * (local SQL, remote API, etc.)
 */

import type { Transaction, Rule } from '../types';

/**
 * Database row structure for transactions
 */
export type TransactionRow = {
  id: string;
  date: string;
  description: string;
  amount: number;
  category: string;
  person: string;
  source: string;
  bank: string;
  dedupe_id: string | null;
  post_date: string | null;
  type: string | null;
  memo: string | null;
  balance: number | null;
  raw_data: string | null;
  file_id: string | null;
  display_name: string | null;
  category_source: string | null;
  confidence: number | null;
  category_method: string | null;
  is_recurring: number;
  recurring_type: string | null;
  excluded: number;
  exclusion_reason: string | null;
  excluded_by: string | null;
  excluded_at: string | null;
  is_split: number;
  split_parent_id: string | null;
  split_children: string | null;
};

/**
 * Database row structure for rules
 */
export type RuleRow = {
  id: string;
  pattern: string;
  category: string;
  person: string | null;
  normalized_name: string | null;
  mark_as_recurring: number;
  recurring_type: string | null;
  exclude_from_analytics: number;
  enabled: number;
  created_at: string;
  updated_at: string;
};

/**
 * Convert a database row to a Transaction object
 *
 * @param row - Database row with snake_case columns
 * @returns Transaction object with camelCase properties
 */
export function rowToTransaction(row: TransactionRow): Transaction {
  return {
    id: row.id,
    date: row.date,
    description: row.description,
    amount: row.amount,
    category: row.category,
    person: row.person,
    source: row.source,
    bank: row.bank,
    dedupeId: row.dedupe_id,
    postDate: row.post_date,
    type: row.type,
    memo: row.memo,
    balance: row.balance,
    rawData: row.raw_data ? JSON.parse(row.raw_data) : undefined,
    fileId: row.file_id,
    displayName: row.display_name,
    categorySource: row.category_source,
    confidence: row.confidence,
    categoryMethod: row.category_method,
    isRecurring: !!row.is_recurring,
    recurringType: row.recurring_type,
    excluded: !!row.excluded,
    exclusionReason: row.exclusion_reason,
    excludedBy: row.excluded_by,
    excludedAt: row.excluded_at,
    isSplit: !!row.is_split,
    splitParentId: row.split_parent_id,
    splitChildren: row.split_children ? JSON.parse(row.split_children) : undefined
  };
}

/**
 * Convert a database row to a Rule object
 *
 * @param row - Database row with snake_case columns
 * @returns Rule object with camelCase properties
 */
export function rowToRule(row: RuleRow): Rule {
  return {
    id: row.id,
    pattern: row.pattern,
    category: row.category,
    person: row.person,
    normalizedName: row.normalized_name,
    markAsRecurring: !!row.mark_as_recurring,
    recurringType: row.recurring_type,
    excludeFromAnalytics: !!row.exclude_from_analytics,
    enabled: !!row.enabled,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

/**
 * Convert Transaction to database row format
 *
 * @param txn - Transaction object
 * @returns Database row object with snake_case columns
 */
export function transactionToRow(txn: Transaction): Omit<TransactionRow, 'created_at' | 'updated_at'> {
  return {
    id: txn.id,
    date: txn.date,
    description: txn.description,
    amount: txn.amount,
    category: txn.category,
    person: txn.person,
    source: txn.source,
    bank: txn.bank,
    dedupe_id: txn.dedupeId || null,
    post_date: txn.postDate || null,
    type: txn.type || null,
    memo: txn.memo || null,
    balance: txn.balance || null,
    raw_data: txn.rawData ? JSON.stringify(txn.rawData) : null,
    file_id: txn.fileId || null,
    display_name: txn.displayName || null,
    category_source: txn.categorySource || null,
    confidence: txn.confidence || null,
    category_method: txn.categoryMethod || null,
    is_recurring: txn.isRecurring ? 1 : 0,
    recurring_type: txn.recurringType || null,
    excluded: txn.excluded ? 1 : 0,
    exclusion_reason: txn.exclusionReason || null,
    excluded_by: txn.excludedBy || null,
    excluded_at: txn.excludedAt || null,
    is_split: txn.isSplit ? 1 : 0,
    split_parent_id: txn.splitParentId || null,
    split_children: txn.splitChildren ? JSON.stringify(txn.splitChildren) : null
  };
}
